/**
 * Уведомления: лента колокольчика и история (§18.4–18.5).
 *
 * Один эндпоинт на оба применения. Колокольчик — та же лента с малым
 * limit, и второй эндпоинт означал бы два расходящихся представления
 * одной и той же строки.
 */

import type { NotificationCategory } from "@taskflow/contracts";

import { api } from "../api";

/** Поле, изменившееся в задаче: «было → стало». */
export interface NotificationChange {
  field: string;
  before: string | null;
  after: string | null;
}

/**
 * Состав уведомления. Самодостаточен: задачу могли переименовать,
 * перенести или заархивировать, а уведомление обязано рассказывать
 * о том, что произошло ТОГДА.
 */
export interface NotificationPayload {
  taskKey?: string | null;
  taskTitle?: string | null;
  boardId?: string | null;
  boardName?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  /** Статус задачи на момент события: системный тег или название столбца. */
  statusLabel?: string | null;
  url?: string | null;
  actorId?: string | null;
  actorName?: string | null;
  actors?: { id: string; name: string | null }[];
  changes?: NotificationChange[];
  commentId?: string | null;
  commentExcerpt?: string | null;
  filename?: string | null;
  subtaskKey?: string | null;
  subtaskTitle?: string | null;
  subtaskState?: "ADDED" | "DONE";
  reason?: string | null;
  /** Архивация столбца целиком: одно уведомление на все его задачи. */
  columnName?: string | null;
  archivedCount?: number;
  dueDateLabel?: string | null;
  daysLeft?: number;
  /** Заметки: приглашение и напоминание. */
  noteId?: string | null;
  noteTitle?: string | null;
  itemText?: string | null;
  role?: "VIEW" | "EDIT";
  remindAt?: string | null;
  /** Отпуска: период, коллеги в пересечении, что сделал руководитель. */
  rangeLabel?: string | null;
  colleagues?: string[];
  vacationAction?: "CREATED" | "UPDATED" | "DELETED";
  /** Последнее уведомление подписки: задача ушла из наблюдаемого объекта. */
  watchLeft?: {
    targetType: "COLUMN" | "BOARD" | "PROJECT";
    fromName: string | null;
    toName: string | null;
  } | null;
  occurredAt?: string;
}

export interface NotificationItem {
  id: string;
  type: NotificationCategory;
  payload: NotificationPayload;
  read: boolean;
  /** Сколько событий схлопнуто в эту запись. Единица — обычный случай. */
  count: number;
  taskKey: string | null;
  projectId: string | null;
  createdAtLabel: string;
  createdAt: string;
}

export interface NotificationPage {
  data: NotificationItem[];
  unreadCount: number;
  /** Проекты, по которым у человека вообще есть уведомления. */
  projects: { id: string; name: string }[];
  pageInfo: { hasMore: boolean; offset: number };
}

export interface NotificationQuery {
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
  types?: NotificationCategory[];
  projectId?: string;
}

export const notificationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    notifications: build.query<NotificationPage, NotificationQuery>({
      query: (params) => ({ method: "notifications.list", params }),
      providesTags: ["Notification"],
    }),

    markNotificationsRead: build.mutation<
      { ok: boolean; unreadCount: number },
      { ids: string[]; read?: boolean }
    >({
      query: ({ ids, read = true }) => ({
        method: "notifications.markRead",
        params: { ids, read },
      }),
      invalidatesTags: ["Notification"],
    }),

    markAllNotificationsRead: build.mutation<{ ok: boolean; unreadCount: number }, void>({
      query: () => ({ method: "notifications.markAllRead" }),
      invalidatesTags: ["Notification"],
    }),
  }),
});

export const {
  useNotificationsQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
} = notificationsApi;
